import { useState, useEffect } from "react";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  TrendingUp, 
  Target, 
  Activity, 
  Zap, 
  Shield, 
  Eye,
  ArrowRight,
  Lock,
  Crown,
  Loader2,
  Settings
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DiscordSync } from "@/components/DiscordSync";

const Dashboard = () => {
  const { user, session, subscription, checkSubscription } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleManageSubscription = async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/pricing');
  };
  const features = [
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Real-Time Tracking",
      description: "Monitor every signal as it happens with live updates and instant notifications"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Full Transparency",
      description: "Complete visibility into every trade signal, entry, exit, and performance metric"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Portfolio Growth",
      description: "Watch your portfolio grow with detailed charts and performance analytics"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Win Rate Analytics",
      description: "Track success rates across all trader signals with comprehensive statistics"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "ROI Tracking",
      description: "Monitor your return on investment with precise calculations and projections"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Live Performance",
      description: "Real-time performance metrics for every signal and trader on the platform"
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <SEO 
        title="Live Analytics Dashboard - Real-Time Crypto Signal Performance | Oraculus:X Premium"
        description="Access professional-grade analytics dashboard with real-time signal tracking, portfolio growth monitoring, win rate statistics, and ROI tracking. Premium members only."
      />
      <div className="fixed inset-0 bg-gradient-mesh animate-mesh-move opacity-40" />
      <div className="fixed inset-0 bg-gradient-opal-radial animate-gradient-shift" style={{ backgroundSize: '200% 200%' }} />
      
      <main className="relative z-10 pt-20 md:pt-32">
        <section className="py-20">
          <div className="container px-4">
            {/* Hero Section */}
            <div className="text-center mb-16 space-y-6 animate-fade-in">
              {/* Subscription Status */}
              {subscription && (
                <Card className="max-w-2xl mx-auto p-6 bg-gradient-primary relative overflow-hidden mb-8">
                  <div className="absolute inset-0 bg-card/90 backdrop-blur-2xl" />
                  <div className="relative z-10 space-y-4">
                    <div className="flex items-center justify-center gap-2">
                      <Crown className="w-5 h-5 text-primary" />
                      <span className="text-lg font-semibold">
                        {subscription.subscribed ? "Premium Active" : "No Active Subscription"}
                      </span>
                    </div>
                    {subscription.subscribed && subscription.subscription_end && (
                      <p className="text-sm text-muted-foreground">
                        Renews on {new Date(subscription.subscription_end).toLocaleDateString()}
                      </p>
                    )}
                    <Button
                      onClick={subscription.subscribed ? handleManageSubscription : handleSubscribe}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      className="mt-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Settings className="mr-2 h-4 w-4" />
                          {subscription.subscribed ? "Manage Subscription" : "Subscribe Now"}
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              )}

              {/* Discord Integration */}
              {subscription?.subscribed && (
                <div className="max-w-2xl mx-auto mb-8">
                  <DiscordSync />
                </div>
              )}
              
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Crown className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">Premium Feature</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold">
                Live Performance Analytics{" "}
                <span className="bg-gradient-primary bg-clip-text text-transparent">Dashboard v1.0</span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Real-time tracking and transparency for every signal. Watch your portfolio grow with detailed metrics, win rates, and ROI tracking across all trader signals.
              </p>
              
              {/* Launch Dashboard CTA */}
              <div className="pt-8">
                <Card className="p-8 md:p-12 bg-gradient-primary relative overflow-hidden max-w-2xl mx-auto">
                  <div className="absolute inset-0 bg-card/90 backdrop-blur-2xl" />
                  <div className="relative z-10 space-y-6">
                    <div className="flex items-center justify-center gap-3">
                      <Shield className="w-8 h-8 text-primary" />
                      <h3 className="text-2xl font-bold">Premium Members Only</h3>
                    </div>
                    <p className="text-muted-foreground">
                      Access the dashboard through Discord login. Available exclusively for subscribed members.
                    </p>
                    <Button 
                      size="lg" 
                      className="w-full bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-lg group"
                      asChild
                    >
                      <a 
                        href="https://dashboard.oraculusx.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2"
                      >
                        <Lock className="w-5 h-5" />
                        Launch Dashboard
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      Login with your Discord account to access premium analytics
                    </p>
                  </div>
                </Card>
              </div>
            </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className="p-6 bg-card/30 backdrop-blur-xl border-border hover:border-primary/50 transition-all hover:scale-105 animate-fade-in group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-gradient-primary group-hover:shadow-glow-primary transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

            {/* Dashboard Preview Section */}
            <div className="text-center space-y-8">
              <Card className="p-8 md:p-12 bg-gradient-primary relative overflow-hidden">
                <div className="absolute inset-0 bg-card/80 backdrop-blur-2xl" />
                <div className="relative z-10 space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Transform Your Trading Experience
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                    Get instant access to professional-grade analytics that help you make informed decisions. 
                    Track performance, analyze trends, and maximize your returns with data-driven insights.
                  </p>
                  <div className="grid md:grid-cols-3 gap-6 pt-6">
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-primary">Real-Time</div>
                      <div className="text-sm text-muted-foreground">Live signal tracking</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-primary">5-10</div>
                      <div className="text-sm text-muted-foreground">Elite traders monitored</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-primary">24/7</div>
                      <div className="text-sm text-muted-foreground">Continuous analytics</div>
                    </div>
                  </div>
                </div>
              </Card>

              <div className="pt-8">
                <p className="text-lg text-muted-foreground mb-4">
                  {subscription?.subscribed ? "Manage your subscription below" : "Not a premium member yet?"}
                </p>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/10"
                  onClick={subscription?.subscribed ? handleManageSubscription : handleSubscribe}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      {subscription?.subscribed ? <Settings className="w-5 h-5" /> : <Crown className="w-5 h-5" />}
                      {subscription?.subscribed ? "Manage Subscription" : "Upgrade to Premium"}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
